import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { calculateHintCost } from './calculateHintCost';
import { jsx as ___EmotionJSX } from "@emotion/react";
export const MultiStepInstructionsContext = createContext(null);
export const useMultiStepInstructionsContext = () => {
  const context = useContext(MultiStepInstructionsContext);
  if (!context) {
    throw new Error('`useMultiStepInstructionsContext` must be used within `MultiStepInstructionsContextProvider`');
  }
  return context;
};
export const MultiStepInstructionsContextProvider = _ref => {
  let {
    children,
    hasQuestionAtTheEnd,
    instructionSteps: externalInstructionSteps,
    isHTMLSanitized = false,
    xp
  } = _ref;
  const [xpWon, setXpWon] = useState(xp);
  const [isInstructionHintDisplayed, setIsInstructionHintDisplayed] = useState(false);
  const [isExerciseHintDisplayed, setIsExerciseHintDisplayed] = useState(false);
  const [isExerciseHintTaken, setIsExerciseHintTaken] = useState(false);
  const [numberOfHintsTaken, setNumberOfHintsTaken] = useState(0);
  const [activeInstructionIndex, setActiveInstructionIndex] = useState(0);
  const [isSolutionDisplayed, setIsSolutionDisplayed] = useState(false);
  const [instructionSteps, setInstructionSteps] = useState(externalInstructionSteps.map(instructionStep => _objectSpread(_objectSpread({}, instructionStep), {}, {
    hintRatingStatus: 'NONE',
    hintTaken: false
  })));
  const [questionHintRatingStatus, setQuestionHintRatingStatus] = useState('NONE');
  const [exerciseHintRatingStatus, setExerciseHintRatingStatus] = useState('NONE');
  const numberOfTotalSteps = hasQuestionAtTheEnd ? instructionSteps.length + 1 : instructionSteps.length;
  const setInstructionHintRatingStatus = useCallback(_ref2 => {
    let {
      hintStatus,
      stepIndex
    } = _ref2;
    setInstructionSteps(instructionSteps.map((item, index) => {
      if (index !== stepIndex) {
        return item;
      }
      return _objectSpread(_objectSpread({}, item), {}, {
        hintRatingStatus: hintStatus
      });
    }));
  }, [instructionSteps]);
  const incrementNumberOfHintsTaken = useCallback(() => {
    setNumberOfHintsTaken(numberOfHintsTaken + 1);
  }, [numberOfHintsTaken]);
  const updateXpWon = useCallback(() => {
    const calculationResult = xpWon - calculateHintCost({
      numberOfHintsAvailable: numberOfTotalSteps,
      numberOfHintsTaken,
      totalHintCostXp: 0.3 * xp
    });
    const updatedXp = calculationResult >= 0 ? calculationResult : 0;
    setXpWon(updatedXp);
  }, [numberOfHintsTaken, numberOfTotalSteps, xp, xpWon]);
  const showInstructionHint = useCallback(stepIndex => {
    setIsInstructionHintDisplayed(true);
    setInstructionSteps(instructionSteps.map((item, index) => {
      if (index !== stepIndex) {
        return item;
      }
      if (!item.hintTaken) {
        updateXpWon();
        incrementNumberOfHintsTaken();
      }
      return _objectSpread(_objectSpread({}, item), {}, {
        hintTaken: true
      });
    }));
  }, [incrementNumberOfHintsTaken, instructionSteps, updateXpWon]);
  const showExerciseHint = useCallback(() => {
    setIsExerciseHintDisplayed(true);
    if (!isExerciseHintTaken) {
      updateXpWon();
      incrementNumberOfHintsTaken();
      setIsExerciseHintTaken(true);
    }
  }, [incrementNumberOfHintsTaken, isExerciseHintTaken, updateXpWon]);
  const hideInstructionHint = () => {
    setIsInstructionHintDisplayed(false);
  };
  const hideExerciseHint = () => {
    setIsExerciseHintDisplayed(false);
  };
  const showSolution = useCallback(() => {
    setXpWon(0);
    hideInstructionHint();
    hideExerciseHint();
    setActiveInstructionIndex(numberOfTotalSteps - 1);
    setIsSolutionDisplayed(true);
  }, [numberOfTotalSteps]);
  const store = useMemo(() => ({
    activeInstructionIndex,
    exerciseHintRatingStatus,
    hasQuestionAtTheEnd,
    hideExerciseHint,
    hideInstructionHint,
    instructionSteps,
    isExerciseHintDisplayed,
    isHTMLSanitized,
    isInstructionHintDisplayed,
    isSolutionDisplayed,
    numberOfTotalSteps,
    questionHintRatingStatus,
    setActiveInstructionIndex,
    setExerciseHintRatingStatus,
    setInstructionHintRatingStatus,
    setQuestionHintRatingStatus,
    showExerciseHint,
    showInstructionHint,
    showSolution,
    xpWon
  }), [activeInstructionIndex, exerciseHintRatingStatus, hasQuestionAtTheEnd, instructionSteps, isExerciseHintDisplayed, isInstructionHintDisplayed, isSolutionDisplayed, numberOfTotalSteps, questionHintRatingStatus, setInstructionHintRatingStatus, isHTMLSanitized, showExerciseHint, showInstructionHint, showSolution, xpWon]);
  return ___EmotionJSX(MultiStepInstructionsContext.Provider, {
    value: store
  }, children);
};
MultiStepInstructionsContextProvider.displayName = 'MultiStepInstructionsContextProvider';